import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PenLine, Eraser } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const trimCanvas = (canvas) => {
  const ctx = canvas.getContext('2d');
  const { width, height } = canvas;
  const px = ctx.getImageData(0, 0, width, height).data;
  let [x0, y0, x1, y1] = [width, height, 0, 0];
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    if (px[(y * width + x) * 4 + 3] > 0) { x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y); }
  }
  if (x1 <= x0) return null;
  const out = document.createElement('canvas');
  out.width = x1 - x0 + 12; out.height = y1 - y0 + 12;
  out.getContext('2d').drawImage(canvas, x0 - 6, y0 - 6, out.width, out.height, 0, 0, out.width, out.height);
  return out.toDataURL('image/png');
};

export const SignaturePad = ({ token }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadSaved = () => axios.get(`${API}/pas-banking/signature`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' })
    .then(res => setSaved(URL.createObjectURL(res.data))).catch(() => setSaved(null));

  useEffect(() => {
    loadSaved();
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineWidth = 2.4; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#1c2f6e';
  }, [token]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return [(e.clientX - r.left) * (canvasRef.current.width / r.width), (e.clientY - r.top) * (canvasRef.current.height / r.height)];
  };
  const start = (e) => { drawing.current = true; canvasRef.current.setPointerCapture(e.pointerId); const ctx = canvasRef.current.getContext('2d'); ctx.beginPath(); ctx.moveTo(...pos(e)); };
  const move = (e) => { if (!drawing.current) return; const ctx = canvasRef.current.getContext('2d'); ctx.lineTo(...pos(e)); ctx.stroke(); setHasInk(true); };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); setHasInk(false); };

  const save = async () => {
    const dataUrl = trimCanvas(canvasRef.current);
    if (!dataUrl) { toast.error('Draw your signature first'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/pas-banking/signature`, { image_base64: dataUrl }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message); clear(); loadSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save signature'); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6" data-testid="signature-pad-card">
      <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2"><PenLine className="w-5 h-5 text-sky-400" /> Your check signature</h3>
      <p className="text-slate-400 text-sm mb-4">Sign with your mouse or finger. It prints on every check you issue from now on.</p>
      {saved && (
        <div className="mb-4">
          <p className="text-xs text-slate-500 mb-1">Current signature</p>
          <div className="bg-white rounded-lg p-2 inline-block"><img src={saved} alt="Saved signature" className="h-12" data-testid="saved-signature-image" /></div>
        </div>
      )}
      <canvas ref={canvasRef} width={720} height={200} onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end}
        className="w-full h-[150px] bg-white rounded-lg touch-none cursor-crosshair border-b-4 border-slate-300" data-testid="signature-canvas" />
      <div className="flex gap-2 mt-3">
        <button onClick={clear} data-testid="signature-clear-btn" className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm flex items-center gap-1"><Eraser className="w-4 h-4" /> Clear</button>
        <button onClick={save} disabled={!hasInk || busy} data-testid="signature-save-btn" className="px-5 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">{busy ? 'Saving...' : 'Save signature'}</button>
      </div>
    </div>
  );
};
